import { useMutation } from 'convex/react';
import { api } from '../../api';
import { useUserDevice } from './useUserDevice';
import { useAppCategories } from './useAppCategories';

type AppModeBackend = 'whitelist' | 'blacklist' | 'categories';
type AppModeUI = 'approved-only' | 'open-blocklist' | 'apps-by-category';

const uiToBackend: Record<AppModeUI, AppModeBackend> = {
  'approved-only': 'whitelist',
  'open-blocklist': 'blacklist',
  'apps-by-category': 'categories',
};

const backendToUI: Record<AppModeBackend, AppModeUI> = {
  whitelist: 'approved-only',
  blacklist: 'open-blocklist',
  categories: 'apps-by-category',
};

/**
 * Infer app mode from appFilter when no explicit appMode field exists (backwards compat).
 */
const inferAppMode = (appFilter: any): AppModeBackend => {
  if (appFilter === true) return 'blacklist';

  if (typeof appFilter === 'object' && appFilter !== null) {
    const values = Object.values(appFilter);
    const hasAtLeastOneAllowed = values.some((config: any) => {
      if (typeof config === 'boolean') return config === true;
      if (typeof config === 'object' && config !== null) {
        return config.tagFilter === true || (typeof config.tagFilter === 'object' && config.tagFilter !== null);
      }
      return false;
    });
    return hasAtLeastOneAllowed ? 'categories' : 'whitelist';
  }

  return 'whitelist';
};

export const useDeviceAppMode = () => {
  const { device, deviceConfig, isLoading: isDeviceLoading } = useUserDevice();
  const { deviceAppConfig } = useAppCategories();
  const updateApps = useMutation(api.devices.apps.update);

  const deviceId = device?._id;

  // Read explicit appMode from backend, fall back to inference
  const explicitMode = (deviceAppConfig as any)?.appMode as AppModeBackend | undefined;
  const inferredMode = deviceAppConfig ? inferAppMode(deviceAppConfig.appFilter) : 'whitelist';
  const currentModeBackend = explicitMode ?? inferredMode;
  const currentMode: AppModeUI = backendToUI[currentModeBackend] ?? 'approved-only';

  const setAppMode = async (uiModeId: string) => {
    if (!deviceId) throw new Error('No device');
    const backendMode = uiToBackend[uiModeId as AppModeUI];
    if (!backendMode) throw new Error(`Unknown UI mode: ${uiModeId}`);

    await updateApps({ device: deviceId as any, appMode: backendMode } as any);
  };

  return {
    currentMode,
    isLoading: isDeviceLoading || deviceAppConfig === undefined,
    setAppMode,
  };
};
